import { HttpClient, HttpHandler, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AuthData } from '../models';
import { AppSettingsService } from './app-settings.service';
import { hd } from './helpdesk.typings';
import { BehaviorSubject, Observable, Subscription, of } from 'rxjs';
import { delay, first, switchMap, tap } from 'rxjs/operators';

export interface ILoginData {
  userName: string;
  password: string;
}

export interface IToken {
  expireTime: number;
}

export interface IAuthData {
  claims: hd.ISotiAssistClaims;
}

export interface IAuthDetails extends IAuthData {
  isAuth: boolean;
}

/**
 * When 75% of token lifetime is passed, will trigger token refresh
 */
const DEFAULT_TOKEN_REFRESH_TIMEOUT_FACTOR = 0.75;
export const LOCALSTORAGE_AUTHDETAILS = 'ls.authorizationData';

/**
 * local storage key to track whether to show the welcome dialog to user in the current browser session
 */
export const LOCALSTORAGE_ISWELCOMEREQUIRED = 'IsWelcomeRequired';

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
  private isAuthenticatedPromise: Promise<IAuthDetails>;
  private $http: HttpClient;
  private timer: Subscription;
  private retryTimer;

  private _loginUrlPromise: Promise<string>;

  private _sessionExpired$: BehaviorSubject<boolean>;
  public sessionExpired$: Observable<boolean>;

  constructor(
    httpHandler: HttpHandler,
    @Inject('LOCALSTORAGE') private localStorage: Storage,
    private appSettingsService: AppSettingsService,
    private router: Router,
    @Inject('SESSIONSTORAGE') private sessionStorage: Storage
  ) {
    this.$http = new HttpClient(httpHandler);
    this._sessionExpired$ = new BehaviorSubject<boolean>(this.isExpiredSession);
    this.sessionExpired$ = this._sessionExpired$.asObservable();
  }

  public getLoginUrlAsync(): Promise<string> {
    if (this._loginUrlPromise) {
      return this._loginUrlPromise;
    }
    this._loginUrlPromise = new Promise<string>(async (resolve, reject) => {
      try {
        if (
          window.location.pathname.indexOf('/sotixsight/expiredSession') === -1 &&
          window.location.pathname.indexOf('/sotixsight/login') === -1
        ) {
          let state = 'https://' + window.location.host + '/';
          const pathComponents = window.location.pathname.split('/');
          if (pathComponents && pathComponents.length > 1) {
            state += pathComponents.filter(comp => !!comp).join('/');
          }
          state += window.location.hash;
          state += window.location.search;

          // This builds XSight authorize endpoint URL which we redirect to regardless if
          // native log-in or MobiControl log-in (via another authorization code) will occur
          // afterwards. It will be handled by logic on backend.
          await this.appSettingsService.loadSettings();
          const url = new URL(
            `https://${window.location.host}${
              this.appSettingsService.accountServiceUrl
            }connect/authorize?response_type=code&client_id=${
              this.appSettingsService.clientId
            }&scope=openid&redirect_uri=${encodeURIComponent(window.location.href)}&state=${encodeURIComponent(state)}`
          );
          resolve(url.toString());
        }

        // Safely handle repeated login redirects (workaround?)
        const search = new URLSearchParams(window.location.search);
        const redirect = new URL(search.get('redirectUrl'), window.location.origin);
        redirect.searchParams.set(
          'redirect_uri',
          decodeURI(decodeURIComponent(decodeURIComponent(redirect.searchParams.get('redirect_uri'))))
        );
        redirect.searchParams.set('state', decodeURIComponent(redirect.searchParams.get('state')));
        resolve(redirect.toString());
      } catch (error) {
        reject(error);
      }
    });

    return this._loginUrlPromise;
  }

  /** See: IAuthenticationService.login
   *
   * @param revokeCookie: preset to true, only used by SSP
   *
   */
  login = (loginData: ILoginData) => {
    const query = new HttpParams()
      .set('grantType', 'password')
      .set('userName', loginData.userName)
      .set('password', loginData.password)
      .set('clientId', this.appSettingsService.clientId);
    /** Explicitly clear any authorization data before logging in */
    this.logOut({ redirect: false });

    return new Promise<IToken>((resolve, reject) => {
      this.$http
        .post<IToken>(this.appSettingsService.accountServiceUrl + 'login', query.toString(), {
          headers: new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded'),
        })
        .subscribe(
          (response: IToken) => {
            // Store the user's authorization data
            this.setAuthData({ UserName: loginData.userName, ExpireTime: response.expireTime } as hd.ISotiAssistClaims);

            // Complete the async promise
            resolve(response);
          },
          error => {
            this.logOut({ redirect: false });
            reject(error);
          }
        );
    });
  };

  /** See: IAuthenticationService.tokenRefresh */
  refreshSession = (): Promise<IToken> => {
    const query: string = new HttpParams({
      fromObject: {
        grantType: 'refresh_token',
        clientId: this.appSettingsService.clientId,
        scope: 'openid, profile, offline_access',
      },
    }).toString();

    return new Promise<IToken>((resolve, reject) => {
      this.$http
        .post<IToken>(this.appSettingsService.accountServiceUrl + 'login', query, {
          headers: new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded'),
        })
        .subscribe(
          (response: IToken) => {
            if (!response) {
              this.logoutExpiredSession();
              reject(new Error('Token refresh failed'));
            } else {
              const authDetails = this.getAuthDetails();

              // Update expire time claim
              authDetails.claims.ExpireTime = response.expireTime;

              // Store the user's authorization data - get the current data first so that we don't have to re-request userName
              this.setAuthData(authDetails.claims);

              // Complete the async promise
              resolve(response);
            }
          },
          err => {
            /** Explicitly clear any authorization data */
            this.logoutExpiredSession();

            reject(err);
          }
        );
    });
  };

  public logOutInProgress = false;

  /**
   * Handles log-out.
   *
   * IAuthenticationService.logOut
   *
   * @param redirect: whether to redirect to the log-in page
   */
  public logOut = ({ redirect = false }: { redirect: boolean }) => {
    if (this.logOutInProgress) return;
    this.logOutInProgress = true;
    this.clearLocalSession();

    if (redirect) {
      this.getLoginUrlAsync().then(loginUrl => {
        window.location.href = `${this.appSettingsService.accountServiceUrl}logout?redirectUrl=${encodeURIComponent(
          loginUrl
        )}`;
      });
      return;
    }

    this.clearBackendSession$().subscribe({
      next: _ => {
        this.logOutInProgress = false;
      },
      error: _ => {
        this.logOutInProgress = false;
      },
    });
  };

  public set isActiveSession(isActive: boolean) {
    this.setSessionStorage('activeSession', isActive.toString());
  }

  public set isExpiredSession(isExpired: boolean) {
    this.setSessionStorage('expiredSession', isExpired.toString());
    this._sessionExpired$.next(isExpired);
  }

  public get isActiveSession(): boolean {
    return this.getSessionStorage('activeSession') === 'true';
  }

  public get isExpiredSession(): boolean {
    return this.getSessionStorage('expiredSession') === 'true';
  }

  setSessionStorage = (key: string, value: string) => {
    this.sessionStorage.setItem(key, value);
  };

  getSessionStorage = (key: string): string => {
    return this.sessionStorage.getItem(key);
  };

  clearAuthDetails = () => {
    this.localStorage.removeItem(LOCALSTORAGE_AUTHDETAILS);
    this.localStorage.removeItem(LOCALSTORAGE_ISWELCOMEREQUIRED);
    this.isAuthenticatedPromise = null;
    this.stopRefreshSessionTimer();
  };

  /** See: authenticationService.isAuthenticated */
  isAuthenticated = (): Promise<IAuthDetails> => {
    // Return the current promise if already processing a request
    if (!this.isAuthenticatedPromise) {
      // If the user is authenticated, but authentication has expired, then request a refresh_token
      this.isAuthenticatedPromise = new Promise<IAuthDetails>((resolve, reject) => {
        const authDetails = this.getAuthDetails();

        if (authDetails.isAuth && this.isExpired()) {
          // Session is expired: attempt a session refresh - otherwise fail.
          this.refreshSession()
            .then(_ => resolve(this.getAuthDetails()))
            .catch(err => reject(err));
        } else if (!authDetails.isAuth) {
          // We're definitely not authenticated, so reject this as a 401
          reject({ type: 'http', status: 401 });
        } else {
          resolve(authDetails);
        }
      });
    }

    this.isAuthenticatedPromise.catch(error => {
      if (error.status === 401) {
        this.isAuthenticatedPromise = null;
      }
    });

    return this.isAuthenticatedPromise;
  };

  /** See: authenticationService.isAuthPage */
  isAuthPage = (): boolean => {
    const result =
      window.location.href.toLocaleLowerCase().indexOf('login') > -1 ||
      window.location.href.toLocaleLowerCase().indexOf('signin') > -1 ||
      window.location.href.toLocaleLowerCase().indexOf('signout') > -1 ||
      window.location.href.toLocaleLowerCase().indexOf('error') > -1;
    return result;
  };

  /** Gets the current authorization data from local storage */
  getAuthDetails = (): IAuthDetails => {
    const authDetails: IAuthDetails = {
      isAuth: false,
      claims: null,
    };

    const authData: AuthData = JSON.parse(this.localStorage.getItem(LOCALSTORAGE_AUTHDETAILS));
    if (authData) {
      authDetails.isAuth = true;
      authDetails.claims = authData.claims;
    }

    return authDetails;
  };

  /**
   * Returns true if the current authentication details have expired
   */
  isExpired = (): boolean => {
    const authDetails = this.getAuthDetails();

    // Return if no expire time
    if (!authDetails?.claims?.ExpireTime) {
      return false;
    }

    const currentTime = Date.now() / 1000;
    return authDetails.claims.ExpireTime < currentTime;
  };

  /** Gets a boolean indicating whether user should be welcomed */
  isWelcomeRequired = (): boolean => JSON.parse(this.localStorage.getItem(LOCALSTORAGE_ISWELCOMEREQUIRED)) != false;

  setWelcomeRequired = (isWelcomeRequired: boolean) => {
    this.localStorage.setItem(LOCALSTORAGE_ISWELCOMEREQUIRED, JSON.stringify(isWelcomeRequired));
  };

  startRefreshSessionTimer(resolve: Function = null): Observable<IToken> {
    return this.setRefreshSessionTimer(this.getAuthDetails(), resolve);
  }

  setRefreshSessionTimer = (authData: IAuthData, resolve: Function = null): Observable<IToken> => {
    if (!authData?.claims?.ExpireTime) {
      return;
    }

    if (this.timer) {
      this.stopRefreshSessionTimer();
    }

    const refreshTime = this.getRefreshTokenRefreshTime(authData.claims.ExpireTime);
    if (refreshTime > 0) {
      console.warn(`Setting up refresh token timer for ${refreshTime / 1000} seconds`);
    }

    this.timer = of(true)
      .pipe(
        delay(refreshTime),
        first(),
        switchMap(() => {
          console.warn('Time is up: Attempting to refresh token');
          return this.refreshSession();
        }),
        tap((response: IToken) => {
          if (resolve) {
            resolve(response);
          }
        })
      )
      .subscribe();
  };

  private getRefreshTokenRefreshTime = (expireTime: number): number => {
    const expiresIn = expireTime * 1000 - new Date().getTime();
    const refreshTime = DEFAULT_TOKEN_REFRESH_TIMEOUT_FACTOR * expiresIn;
    return refreshTime >= 0 ? refreshTime : 0;
  };

  stopRefreshSessionTimer = () => {
    try {
      this.timer?.unsubscribe();
      this.timer = null;
    } catch (error: any) {
      console.log(`Error while trying to stop current refresh token ${error}`);
    }
    clearTimeout(this.retryTimer);
  };

  private setAuthData = (claims: hd.ISotiAssistClaims) => {
    const authData: IAuthData = {
      claims,
    };
    this.localStorage.setItem(LOCALSTORAGE_AUTHDETAILS, JSON.stringify(authData));
    this.setRefreshSessionTimer(authData);
  };

  /** get user authorization data from local storage */
  private getClaims = (): hd.ISotiAssistClaims => {
    const authDetails = this.getAuthDetails();
    return authDetails?.claims;
  };
  /** See: IAuthorizationService.isAdminOrTechnician */
  isAdminOrTechnician = (groupid: number): boolean =>
    this.isSystemAdmin() || this.isGroupAdmin(groupid) || this.isGroupTechnician(groupid);

  isSystemAdmin = (): boolean => {
    const claims = this.getClaims();
    return claims != null && claims.SystemAdmin === 'true';
  };

  /** See: IAuthorizationService.isGroupAdmin */
  isGroupAdmin = (groupid: number): boolean => {
    if (groupid == null) {
      // check for null or undefined
      return false;
    }
    const claims = this.getClaims();
    if (!claims || !claims.GroupAdmin) {
      return false;
    }

    const groupAdminArray = claims.GroupAdmin.split(',');
    return groupAdminArray.indexOf(groupid.toString()) > -1;
  };

  isGroupTechnician = (groupid: number): boolean => {
    if (groupid == null) {
      // check for null or undefined
      return false;
    }
    const claims = this.getClaims();
    if (!claims || !claims.GroupTechnician) {
      return false;
    }
    const groupTechnicianArray = claims.GroupTechnician.split(',');
    return groupTechnicianArray.indexOf(groupid.toString()) > -1;
  };

  // Gets the userId of the currently logged in user
  getUserId = (): string => {
    return this.getClaims()?.UserId;
  };

  logoutExpiredSession = () => {
    if (this.logOutInProgress) return;

    this.logOutInProgress = true;

    this.clearLocalSession();
    this.getLoginUrlAsync().then(loginUrl => {
      this.clearBackendSession$().subscribe({
        next: _ => {
          this.logOutInProgress = false;
          this.redirectToExpiredSession(loginUrl);
        },
        error: _ => {
          this.logOutInProgress = false;
          this.redirectToExpiredSession(loginUrl);
        },
      });
    });
  };

  private clearLocalSession = () => {
    this._sessionExpired$.next(true);
    this.clearAuthDetails();
    this.stopRefreshSessionTimer();
  };

  private clearBackendSession$ = () => {
    return this.$http.get(this.appSettingsService.accountServiceUrl + 'logout');
  };

  private redirectToExpiredSession = (loginUrl: string) => {
    this.router.navigate(['/expiredSession'], {
      queryParams: { redirectUrl: loginUrl },
    });
  };
}
